import ReactMarkdown, { type Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { remarkTeamMarkdown } from '@/lib/feedback/teamMarkdown'
import { formatDateTime } from '@/lib/format'
import { cn } from '@/lib/utils'
import type { FeedbackMessage, FeedbackReportDetail } from '@/types/feedback'
import { AttachmentPreview } from './AttachmentPreview'
import { FeedbackReplyForm } from './FeedbackReplyForm'

/**
 * The team writes Markdown on GitHub; links leave the app in an isolated tab.
 * Raw HTML never reaches the page: `remarkTeamMarkdown` keeps only its text.
 */
const TEAM_MARKDOWN: Components = {
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary underline">
      {children}
    </a>
  ),
  p: ({ children }) => <p className="mt-2 first:mt-0">{children}</p>,
  ul: ({ children }) => <ul className="mt-2 list-disc pl-5">{children}</ul>,
  ol: ({ children }) => <ol className="mt-2 list-decimal pl-5">{children}</ol>,
  code: ({ children }) => (
    <code className="rounded bg-muted px-1 py-0.5 font-mono text-sm">{children}</code>
  ),
  pre: ({ children }) => (
    <pre className="mt-2 overflow-x-auto rounded bg-muted p-2 text-sm [&_code]:p-0">{children}</pre>
  ),
  blockquote: ({ children }) => (
    <blockquote className="mt-2 border-l-4 border-primary/40 pl-3 text-muted-foreground">
      {children}
    </blockquote>
  ),
}

const CLOSED_STATUSES = new Set(['resolved', 'not_planned'])

function ThreadMessage({ reportId, message }: { reportId: string; message: FeedbackMessage }) {
  const { t } = useTranslation('feedback')
  const fromTeam = message.author === 'team'
  return (
    <li
      aria-label={fromTeam ? t('thread.fromTeam') : t('thread.fromUser')}
      className={cn('rounded-md border p-3', fromTeam ? 'border-primary/30 bg-primary/5' : 'bg-card')}
    >
      <p className="text-xs text-muted-foreground">
        <span className="font-medium text-foreground">
          {fromTeam ? t('thread.team') : (message.author_name ?? t('thread.user'))}
        </span>
        {' · '}
        {formatDateTime(message.created_at)}
      </p>
      <div className="mt-2 break-words text-sm">
        {fromTeam ? (
          <ReactMarkdown
            remarkPlugins={[
              remarkGfm,
              [remarkTeamMarkdown, { imagePlaceholder: t('thread.imagePlaceholder') }],
            ]}
            components={TEAM_MARKDOWN}
          >
            {message.body}
          </ReactMarkdown>
        ) : (
          <p className="whitespace-pre-wrap">{message.body}</p>
        )}
      </div>
      {message.attachments.length > 0 && (
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {message.attachments.map((attachment) => (
            <AttachmentPreview key={attachment.id} reportId={reportId} attachment={attachment} />
          ))}
        </div>
      )}
    </li>
  )
}

interface FeedbackThreadProps {
  report: FeedbackReportDetail
  /** Only the report's organization answers the team. */
  canReply: boolean
}

export function FeedbackThread({ report, canReply }: FeedbackThreadProps) {
  const { t } = useTranslation('feedback')
  const closed = CLOSED_STATUSES.has(report.status)
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('thread.title')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {report.messages.length > 0 ? (
          <ol className="space-y-3">
            {report.messages.map((message) => (
              <ThreadMessage key={message.id} reportId={report.id} message={message} />
            ))}
          </ol>
        ) : (
          <p className="text-sm text-muted-foreground">{t('thread.empty')}</p>
        )}
        {canReply &&
          (closed ? (
            <p className="text-sm text-muted-foreground">{t('thread.closed')}</p>
          ) : (
            <FeedbackReplyForm reportId={report.id} />
          ))}
      </CardContent>
    </Card>
  )
}
